import type { ConsentScope } from '../../../types/domain/consent/consentInfo';

/**
 * Scopes that describe the user rather than grant the app any capability over CMG data.
 * `offline_access` belongs here: it governs how long a grant lasts, not what it can reach.
 */
const IDENTITY_SCOPES = new Set(['openid', 'profile', 'email', 'offline_access']);

/**
 * What the scope lets the app do, which the DTO does not say. Every CMG API scope pairs a
 * `:read_only` grant with a `:full` one that also writes, and `offline_access` is the only scope
 * that outlives the browser session. A `kind` on the DTO would be better than parsing names here.
 *
 * Anything unrecognised counts as a write. Matching `:full` instead would make `read` the default,
 * so a scope added later — `xc:write`, `orderbook:manage` — would draw an eye over a grant that
 * changes data, and this is the screen where that error is worst in that direction.
 */
export type ScopeKind = 'persistent' | 'write' | 'read';

export const scopeKind = ({ name }: ConsentScope): ScopeKind => {
  if (name === 'offline_access') {
    return 'persistent';
  }

  return IDENTITY_SCOPES.has(name) || name.endsWith(':read_only') ? 'read' : 'write';
};

export const actsOnBehalfOfUser = (scopes: ConsentScope[]) =>
  scopes.some(scope => !IDENTITY_SCOPES.has(scope.name));
